import {
  ConditionalBlocksLogo,
  blankCondition,
  blankPreset,
  blankSet,
  shouldDisplaySeparator,
} from '../../utils/helpers.js';
import { optionsManager } from '../../utils/manager.js';
import ButtonWithConfirm from '../misc/ButtonWithConfirm/index.js';
import CardWithButton from '../misc/CardWithButton/index.js';
import ConditionBuilder from './Builder.js';
import apiFetch from '@wordpress/api-fetch';
const { TextControl, PanelBody, PanelRow, Button, CheckboxControl, TabPanel, Spinner, Icon, Modal } = wp.components;
const { Fragment, useState, useEffect } = wp.element;

const { __ } = wp.i18n;

/**
 * Get the sets of conditions from the currently selected block.
 *
 * @returns array sets.
 */
const getSets = () => {
  const block = wp.data.select('core/block-editor').getSelectedBlock();

  // CB 3+ requires conditions to be be in a set.
  const uncollectedCondition = block?.attributes?.conditionalBlocks?.conditions?.length
    ? block.attributes.conditionalBlocks.conditions
    : false;

  // Move conditions into a new set and wrap it in an array..
  if (uncollectedCondition) {
    return [blankSet({ conditions: uncollectedCondition })];
  }

  return block?.attributes?.conditionalBlocks?.sets ? [...block.attributes.conditionalBlocks.sets] : [];
};

/**
 * Save the set and it's conditions.
 *
 * @param object set
 * @param array conditions
 * @returns
 */
const saveSet = (modifiedSet, modifiedConditions, sets) => {
  const block = wp.data.select('core/block-editor').getSelectedBlock();

  const index = sets.findIndex((x) => x.id === modifiedSet.id);

  // eslint-disable-next-line no-param-reassign
  sets[index] = { ...modifiedSet, conditions: [...modifiedConditions] };

  if (block.clientId) {
    wp.data
      .dispatch('core/block-editor')
      .updateBlockAttributes(block.clientId, { conditionalBlocks: { sets: [...sets] } }, false);
  } else {
    console.log('Unable to get selected block clientId.');
  }
};

/**
 * Save all sets.
 *
 * @param object set
 * @param array conditions
 * @returns
 */
const updateSets = (modifiedSets) => {
  const block = wp.data.select('core/block-editor').getSelectedBlock();

  if (block.clientId) {
    wp.data
      .dispatch('core/block-editor')
      .updateBlockAttributes(block.clientId, { conditionalBlocks: { sets: [...modifiedSets] } }, false);
  }
};

export default function ConditionalBlocksModal({ isOpen, setIsOpen, blockProps }) {
  const { name, attributes, isSelected, setAttributes } = blockProps;

  const [createPresetName, setCreatePresetName] = useState([]);

  const manager = optionsManager();

  const presets = conditionalblocks?.is_pro && manager.get('presets') ? manager.get('presets') : [];

  useEffect(() => {
    if (isOpen) {
      handleOpen();
    } else {
      handleClose();
    }
  }, [isOpen]); // This effect runs when `isOpen` changes

  const handleOpen = () => {
    const sets = getSets();

    // Create a new set as default to reduce clicks.
    if (!sets.length) {
      updateSets([blankSet({ conditions: [blankCondition()] })]);
    }
  };

  const handleClose = () => {
    const sets = getSets();

    // if no changes, clean up the condition defaults
    if (!sets.length || !sets[0]?.conditions[0]?.type) {
      updateSets([]); // Empty.
    }
  };

  const savePresets = (newPresets) => {
    if (conditionalblocks?.is_pro) {
      conditionalblocks.presets = newPresets;
      manager.update({ presets: newPresets }, false);
    }
  };

  const builderTab = () => {
    const sets = getSets();

    const lastSetCondition = [...sets].pop()?.conditions[0]?.type;

    return (
      <div>
        <h2>{__('Manage Conditions', 'conditional-blocks')}</h2>
        <p>The selected block will use the below configuration.</p>
        {conditionalblocks?.is_pro && !conditionalblocks?.is_active && (
          <div>
            <CardWithButton
              content="License activation required from the plugin page."
              buttonText="Activate now"
              buttonUrl={conditionalblocks?.plugins_page_url}
            />
          </div>
        )}
        {!conditionalblocks?.is_pro && (
          <div>
            <CardWithButton
              content="Unlock all visibility conditions with Conditional Blocks Pro"
              buttonText="Get all conditions"
              buttonUrl="https://conditionalblocks.com/pricing/?utm_source=conditional-blocks-free&utm_medium=referral&utm_campaign=modal"
            />
          </div>
        )}
        <div className="conditional-blocks-builder-modal__row">
          {sets.map((set, index) => (
            <div key={set?.id ?? index}>
              <PanelBody
                className="conditional-blocks-builder-modal__single_set"
                title={`Condition Set`}
                scrollAfterOpen={false}
                initialOpen={true}
              >
                <PanelRow>
                  <ConditionBuilder
                    isSet
                    conditions={Array.isArray(set?.conditions) ? [...set.conditions] : []}
                    onChange={(updatedConditions) => {
                      saveSet(set, updatedConditions, sets);
                    }}
                  />
                  <ButtonWithConfirm
                    className={'conditional-blocks-builder-modal__remove_set'}
                    isSecondary
                    buttonText={__('Remove Set', 'conditional-blocks')}
                    onConfirm={() => {
                      const modified = [...sets];
                      // Remove this set..
                      modified.splice(index, 1);
                      updateSets(modified);
                    }}
                  />
                </PanelRow>
              </PanelBody>
              {sets.length && shouldDisplaySeparator(index, sets) && (
                <div className="conditional-block-separator-set">{__('OR', 'conditional-blocks')}</div>
              )}
            </div>
          ))}

          <div className="conditional-blocks-builder-modal__row">
            <div className="conditional-block-new-set">
              <Button
                isPrimary
                disabled={sets.length && !lastSetCondition}
                className="conditional-blocks-builder-modal__add_set"
                icon="insert-after"
                onClick={() => {
                  const tempSets = [...sets];
                  tempSets.push(blankSet({ conditions: [blankCondition()] }));
                  updateSets(tempSets);
                }}
              >
                {sets.length ? __('OR', 'conditional-blocks') : __('New Condition Set', 'conditional-blocks')}
              </Button>
            </div>
          </div>
        </div>
        <div className="conditional-blocks-builder-modal__row ">
          <br />
          <CardWithButton
            buttonUrl={
              conditionalblocks?.is_pro
                ? 'https://conditionalblocks.com/docs/?utm_source=conditional-blocks-pro&utm_medium=referral&utm_campaign=modal'
                : 'https://conditionalblocks.com/docs/?utm_source=conditional-blocks-free&utm_medium=referral&utm_campaign=modal'
            }
          />
        </div>
        {manager.isDevMode && (
          <div>
            <div className="conditional-blocks-builder__spacer" />
            <div className="conditional-blocks-builder-modal__developer_section">
              <div className="conditional-blocks-builder-modal__row ">
                <h3>{__('Developer Mode', 'conditional-blocks')}</h3>
                <p>
                  {__(
                    "Developer mode can be toggled from the settings. All blocks and conditions in Developer Mode are created normally. It's safe to use.",
                    'conditional-blocks',
                  )}
                </p>
              </div>

              <div className="conditional-blocks-builder-modal__row ">
                <h4>Condition Sets</h4>
                <pre>{JSON.stringify(sets, null, 2)}</pre>
              </div>
              <div className="conditional-blocks-builder-modal__row ">
                <h4>Block Attributes</h4>
                <pre>{JSON.stringify(attributes, null, 2)}</pre>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const presetsTab = () => (
    <div>
      <h2>{__('Preset Manager', 'conditional-blocks')}</h2>
      <div className="conditional-blocks-builder-modal__row" style={{ display: 'flex', gap: '16px' }}>
        <div className="conditional-blocks-info-box" style={{ flex: 1 }}>
          <p style={{ margin: 0 }}>
            {__('Reusable condition groups that can be applied to multiple blocks', 'conditional-blocks')}
          </p>
        </div>
        <div className="conditional-blocks-info-box" style={{ flex: 1 }}>
          <p style={{ margin: 0 }}>
            {__('Create presets here → Apply them in the Current Block tab', 'conditional-blocks')}
          </p>
        </div>
      </div>
      {conditionalblocks?.is_pro && conditionalblocks?.can_manage_presets && (
        <div className="conditional-blocks-builder-modal__row" style={{ textAlign: 'center', marginTop: '30px', marginBottom: '30px' }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {manager.isSaving ? (
              <Spinner style={{ margin: 0 }} />
            ) : (
              <Icon className="conditional-blocks__save-icon" icon="cloud" style={{ width: '16px', height: '16px' }} />
            )}
            <strong>{__('Changes save instantly across all blocks', 'conditional-blocks')}</strong>
          </p>
        </div>
      )}

      {!conditionalblocks?.is_pro ? (
        <Fragment>
          <CardWithButton
            content={__('Manage visibility across your entire site with presets', 'conditional-blocks')}
            buttonUrl="https://conditionalblocks.com/pricing/?utm_source=conditional-blocks-free&utm_medium=referral&utm_campaign=modal"
            buttonText={__('Learn more', 'conditional-blocks')}
          />
        </Fragment>
      ) : (
        <Fragment>
          {!conditionalblocks?.can_manage_presets ? (
            <div className="conditional-blocks-builder-modal__row">
              <p style={{ fontStyle: 'italic', color: '#757575' }}>
                {__(
                  'You do not have permission to manage presets. You can still use existing presets in the Condition Builder under the Current Block tab.',
                  'conditional-blocks',
                )}
              </p>
            </div>
          ) : (
            <Fragment>
              <div className="conditional-blocks-builder-modal__row">
                {Object.keys(presets).map((_, index) => {
                  const preset = presets[index];

                  return (
                    <PanelBody
                      className="conditional-blocks-builder-modal__single_preset"
                      title={preset?.name ?? ''}
                      key={preset.id}
                      initialOpen={false}
                    >
                      <PanelRow>
                        <div>
                          <TextControl
                            label={__('Preset Name', 'conditional-blocks')}
                            value={preset.name ?? ''}
                            onChange={(val) => {
                              const modifiedPresets = [...presets];

                              modifiedPresets[index].name = val ?? '';
                              savePresets(modifiedPresets);
                            }}
                            placeholder={__('My Preset..', 'conditional-blocks')}
                          />
                        </div>
                        <ConditionBuilder
                          isPreset
                          conditions={Array.isArray(preset?.conditions) ? [...preset.conditions] : []}
                          onChange={(updatedConditions) => {
                            const modifiedPresets = [...presets];

                            modifiedPresets[index].conditions = [...updatedConditions];
                            savePresets(modifiedPresets);
                          }}
                        />
                        <ButtonWithConfirm
                          isSecondary
                          buttonText={__('Remove Preset', 'conditional-blocks')}
                          onConfirm={() => {
                            const modified = [...presets];
                            // Remove this condition.
                            modified.splice(index, 1);
                            savePresets(modified);
                          }}
                        />
                      </PanelRow>
                    </PanelBody>
                  );
                })}
              </div>

              <div className="conditional-blocks-builder__spacer" />

              <div className="conditional-blocks-builder-modal__row conditional-blocks-content-box">
                <h3 style={{ marginTop: 0 }}>{__('Create Preset')}</h3>
                <TextControl
                  label={__('Preset Name', 'conditional-blocks')}
                  help={__('Give the preset a recognizable name.', 'conditional-blocks')}
                  value={createPresetName}
                  onChange={(newValue) => {
                    setCreatePresetName(newValue);
                  }}
                  placeholder={__('My Preset..', 'conditional-blocks')}
                />
                <Button
                  isSecondary
                  disabled={!createPresetName.length}
                  onClick={() => {
                    const modifiedPresets = [...presets];
                    modifiedPresets.push(blankPreset(createPresetName));
                    savePresets(modifiedPresets);
                    setCreatePresetName('');
                  }}
                >
                  {__('Create Preset', 'conditional-blocks')}
                </Button>
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
      {manager.isDevMode && (
        <div>
          <div className="conditional-blocks-builder__spacer" />
          <div className="conditional-blocks-builder-modal__developer_section">
            <div className="conditional-blocks-builder-modal__row ">
              <h3>{__('Developer Mode', 'conditional-blocks')}</h3>
              <strong>
                {__(
                  "All blocks and conditions in Developer Mode are created normally. It's safe to use.",
                  'conditional-blocks',
                )}
              </strong>
            </div>

            <div className="conditional-blocks-builder-modal__row ">
              <h4>Presets</h4>
              <pre>{JSON.stringify(presets, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const settingsTab = () => (
    <Fragment>
      <h2>{__('Settings')}</h2>
      <div className="conditional-blocks-builder-modal__row ">
        <h3>{__('Responsive Blocks', 'conditional-blocks')}</h3>
        <p>
          {__(
            "Change the breakpoints of blocks with the Responsive (Screen sizes) condition to match your theme's breakpoints if needed.",
            'conditional-blocks',
          )}
        </p>
      </div>
      <div className="conditional-blocks-builder-modal__row ">
        <TextControl
          label={__('Desktop Minimum (px)', 'conditional-blocks')}
          value={manager.get('screensizes', 'device_size_desktop_min')}
          onChange={(newValue) => {
            manager.update({ screensizes: { device_size_desktop_min: newValue } });
          }}
          type="number"
          placeholder="1025"
        />
      </div>
      <div className="conditional-blocks-builder-modal__row ">
        <TextControl
          label={__('Tablet Maximum (px)', 'conditional-blocks')}
          value={manager.get('screensizes', 'device_size_tablet_max')}
          onChange={(newValue) => {
            manager.update({ screensizes: { device_size_tablet_max: newValue } });
          }}
          placeholder="1024"
          type="number"
        />
      </div>
      <div className="conditional-blocks-builder-modal__row ">
        <TextControl
          label={__('Tablet Minimum (px)', 'conditional-blocks')}
          value={manager.get('screensizes', 'device_size_tablet_min')}
          onChange={(newValue) => {
            manager.update({ screensizes: { device_size_tablet_min: newValue } });
          }}
          placeholder="768"
          type="number"
        />
      </div>
      <div className="conditional-blocks-builder-modal__row ">
        <TextControl
          label={__('Mobile Maximum (px)', 'conditional-blocks')}
          value={manager.get('screensizes', 'device_size_mobile_max')}
          onChange={(newValue) => {
            manager.update({ screensizes: { device_size_mobile_max: newValue } });
          }}
          placeholder="767"
          type="number"
        />
      </div>
      {conditionalblocks?.is_pro && (
        <>
          <div className="conditional-blocks-builder-modal__row ">
            <h3>{__('Geolocation Service', 'conditional-blocks')}</h3>
            <p>
              {__(
                'The Geolocation related conditions require an ipinfo.io API key to locate the user. You can read more about this in the documentation.',
                'conditional-blocks',
              )}
            </p>
          </div>
          <div className="conditional-blocks-builder-modal__row ">
            <TextControl
              label={__('API Key', 'conditional-blocks')}
              value={manager.get('ipinfo_api_key') || ''}
              onChange={(newValue) => {
                manager.update({ ipinfo_api_key: newValue });
              }}
              placeholder="Your API key"
              autoComplete="off"
              type="password"
            />
            <Button
              isSecondary
              onClick={async () => {
                const apiKey = manager.get('ipinfo_api_key');
                if (!apiKey) {
                  alert(__('Please enter an API key', 'conditional-blocks'));
                  return;
                }
                try {
                  const response = await apiFetch({
                    path: 'conditional-blocks/v1/validate-geolocation',
                    method: 'POST',
                    data: {
                      api_key: apiKey,
                    },
                  });

                  if (response.success && response.data) {
                    alert(
                      __(
                        `Verification successful! You can now use geolocation conditions. You are located in ${response.data.city}, ${response.data.region}, ${response.data.country}`,
                        'conditional-blocks',
                      ),
                    );
                  } else {
                    throw new Error('Failed to verify API key', { cause: response });
                  }
                } catch (responseError) {
                  console.error('Error verifying API key:', responseError);

                  if (responseError.message) {
                    alert(responseError.message.replace(/\\n/g, '\n'));
                  } else {
                    alert(
                      __(
                        'An error occurred while verifying the API key. Check the browser console for details.',
                        'conditional-blocks',
                      ),
                    );
                  }
                }
              }}
            >
              {__('Verify Connection', 'conditional-blocks')}
            </Button>
          </div>
        </>
      )}
      <div className="conditional-blocks-builder-modal__row ">
        <h3>{__('Additional Settings', 'conditional-blocks')}</h3>
        <p>{__('Please refresh the browser/editor after changer any of these settings..', 'conditional-blocks')}</p>
      </div>
      <div className="conditional-blocks-builder-modal__row">
        <CheckboxControl
          __nextHasNoMarginBottom
          label={__('Open Conditions Builder from Toolbar', 'conditional-blocks')}
          help={__(
            'Shows a button to open Conditions Builder from the block toolbar in addition to the sidebar.',
            'conditional-blocks',
          )}
          checked={manager.get('open_from_toolbar')}
          onChange={() => {
            manager.update({ open_from_toolbar: !manager.get('open_from_toolbar') });
          }}
        />
      </div>
      <div className="conditional-blocks-builder-modal__row">
        <CheckboxControl
          __nextHasNoMarginBottom
          label={__('Only show installed integration conditions', 'conditional-blocks')}
          help={__('Enable this to show conditions from installed plugins only.', 'conditional-blocks')}
          checked={manager.get('only_installed_integrations')}
          onChange={() => {
            manager.update({ only_installed_integrations: !manager.get('only_installed_integrations') });
          }}
        />
      </div>
      <div className="conditional-blocks-builder-modal__row">
        <CheckboxControl
          __nextHasNoMarginBottom
          label={__('Developer Mode', 'conditional-blocks')}
          help={__('Show extra information to help development and debugging.', 'conditional-blocks')}
          checked={manager.isDevMode}
          onChange={() => {
            manager.update({ developer_mode: !manager.isDevMode });
          }}
        />
      </div>
      {manager.isDevMode && (
        <div>
          <div className="conditional-blocks-builder__spacer" />
          <div className="conditional-blocks-builder-modal__developer_section">
            <div className="conditional-blocks-builder-modal__row ">
              <h3>{__('Developer Mode', 'conditional-blocks')}</h3>
            </div>

            <div className="conditional-blocks-builder-modal__row ">
              <h4>Settings Data</h4>
              <pre>{JSON.stringify(manager.data, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );

  return (
    <Fragment>
      {isOpen && (
        <Modal
          icon={<ConditionalBlocksLogo />}
          title={conditionalblocks.is_pro ? 'Conditional Blocks Pro' : 'Conditional Blocks'}
          onRequestClose={() => {
            setIsOpen(false);
          }}
          className="conditional-blocks-builder-modal"
        >
          <div className="conditional-blocks-builder-modal__wrapper">
            <TabPanel
              className="conditional-blocks-builder-modal__tabs"
              activeClass="conditional-blocks-active-tab"
              tabs={[
                {
                  name: 'tab1',
                  title: `${__('Current Block', 'conditional-blocks')} - (${name})`,
                  className: 'tab-one',
                  content: builderTab(),
                },
                {
                  name: 'tab2',
                  title: __('Preset Manager', 'conditional-blocks'),
                  className: 'tab-two',
                  content: presetsTab(),
                },
                {
                  name: 'tab3',
                  title: __('Settings', 'conditional-blocks'),
                  className: 'tab-two',
                  content: settingsTab(),
                },
              ]}
            >
              {(tab) => <div>{tab.content}</div>}
            </TabPanel>
          </div>
        </Modal>
      )}
    </Fragment>
  );
}
